import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { translateRisk } from "../lib/constants";

const ReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [riskReport, setRiskReport] = useState(null);
  const [loadingRiskReport, setLoadingRiskReport] = useState(false);
  const [activeTab, setActiveTab] = useState('risk');

  const fetchReports = async () => {
    try { const response = await api.get('/reports'); setReports(response.data); } 
    catch (error) { console.error('Error fetching reports:', error); } 
    finally { setLoading(false); }
  };

  const fetchRiskReport = async () => {
    setLoadingRiskReport(true);
    try { const response = await api.get('/reports/risk/consolidated'); setRiskReport(response.data); } 
    catch (error) { console.error('Error fetching risk report:', error); toast.error('Erro ao carregar relatório de risco'); } 
    finally { setLoadingRiskReport(false); }
  };

  useEffect(() => { fetchReports(); fetchRiskReport(); }, []);

  const generateReport = async () => {
    setGenerating(true);
    try {
      const response = await api.post('/reports/compliance');
      toast.success(`Relatório gerado! ${response.data.emails_sent} emails enviados.`);
      setSelectedReport(response.data); fetchReports();
    } catch (error) { toast.error('Erro ao gerar relatório'); } 
    finally { setGenerating(false); }
  };

  const viewReport = async (reportId) => {
    try { const response = await api.get(`/reports/${reportId}`); setSelectedReport(response.data); setActiveTab('compliance'); } 
    catch (error) { toast.error('Erro ao carregar relatório'); }
  };

  const downloadPdf = async (reportId) => {
    try {
      const response = await api.get(`/reports/${reportId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', `compliance_report_${reportId}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url); toast.success('PDF descarregado com sucesso!');
    } catch (error) { toast.error('Erro ao descarregar PDF'); }
  };

  const downloadRiskPdf = async () => {
    try {
      const response = await api.get('/reports/risk/consolidated/pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', `relatorio_risco_consolidado_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url); toast.success('PDF descarregado com sucesso!');
    } catch (error) { toast.error('Erro ao descarregar PDF'); }
  };

  const getRiskColor = (level) => {
    const colors = { critical: 'bg-red-100 text-red-700 dark:bg-red-900/30', high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30', medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30', low: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30' };
    return colors[level] || colors.medium;
  };

  const getTypeLabel = (type) => ({ client: 'Cliente', supplier: 'Fornecedor', employee: 'Colaborador' })[type] || type;

  return (
    <div data-testid="reports-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Relatórios</h1>
          <p className="text-muted-foreground">Análise de risco consolidada e relatórios de compliance</p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchRiskReport} disabled={loadingRiskReport} className="px-4 py-2 rounded-lg border border-border font-medium hover:bg-muted transition-colors flex items-center gap-2 disabled:opacity-50" data-testid="refresh-risk-btn">
            <svg className={`w-4 h-4 ${loadingRiskReport ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
            Atualizar
          </button>
          <button onClick={downloadRiskPdf} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors flex items-center gap-2" data-testid="download-risk-pdf-btn">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            Exportar PDF
          </button>
        </div>
      </div>

      <div className="flex border-b border-border">
        <button onClick={() => setActiveTab('risk')} className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'risk' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-testid="tab-risk">Relatório de Risco Consolidado</button>
        <button onClick={() => setActiveTab('compliance')} className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'compliance' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-testid="tab-compliance">Relatórios de Compliance</button>
      </div>

      {activeTab === 'risk' && (
        <div className="space-y-6">
          {loadingRiskReport ? (
            <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>
          ) : riskReport ? (
            <>
              <div className="rounded-xl border bg-card p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                  Resumo Executivo
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  <div className="p-4 rounded-lg bg-muted/50 text-center"><p className="text-3xl font-bold">{riskReport.summary.total_entities}</p><p className="text-xs text-muted-foreground">Total Entidades</p></div>
                  <div className="p-4 rounded-lg bg-muted/50 text-center"><p className="text-3xl font-bold">{riskReport.summary.average_risk_score}</p><p className="text-xs text-muted-foreground">Score Médio</p></div>
                  <div className={`p-4 rounded-lg text-center ${getRiskColor(riskReport.summary.global_risk_level)}`}><p className="text-3xl font-bold uppercase">{translateRisk(riskReport.summary.global_risk_level)}</p><p className="text-xs">Risco Global</p></div>
                  <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-center"><p className="text-3xl font-bold text-amber-600">{riskReport.summary.nif_conflicts_count}</p><p className="text-xs text-muted-foreground">Conflitos NIF</p></div>
                  <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-center"><p className="text-3xl font-bold text-red-600">{riskReport.summary.active_conflicts}</p><p className="text-xs text-muted-foreground">Conflitos Ativos</p></div>
                  <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-center"><p className="text-3xl font-bold text-emerald-600">{riskReport.summary.rgpc_compliance.compliance_rate}%</p><p className="text-xs text-muted-foreground">Taxa RGPC</p></div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="rounded-xl border bg-card p-6">
                  <h2 className="text-lg font-semibold mb-4">Distribuição de Risco</h2>
                  <div className="space-y-3">
                    {[{ level: 'critical', label: 'Crítico', count: riskReport.summary.risk_distribution.critical, color: 'bg-red-500' }, { level: 'high', label: 'Alto', count: riskReport.summary.risk_distribution.high, color: 'bg-orange-500' }, { level: 'medium', label: 'Médio', count: riskReport.summary.risk_distribution.medium, color: 'bg-amber-500' }, { level: 'low', label: 'Baixo', count: riskReport.summary.risk_distribution.low, color: 'bg-emerald-500' }].map((item) => (
                      <div key={item.level} className="flex items-center gap-3">
                        <div className={`w-4 h-4 rounded ${item.color}`}></div>
                        <span className="flex-1">{item.label}</span>
                        <span className="font-bold">{item.count}</span>
                        <div className="w-24 h-2 bg-muted rounded-full overflow-hidden"><div className={`h-full ${item.color}`} style={{ width: `${(item.count / riskReport.summary.total_entities * 100) || 0}%` }}></div></div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-xl border bg-card p-6">
                  <h2 className="text-lg font-semibold mb-4">Conformidade RGPC</h2>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between"><span>Ofertas Conformes</span><span className="font-bold text-emerald-600">{riskReport.summary.rgpc_compliance.compliant}</span></div>
                    <div className="flex items-center justify-between"><span>Ofertas Não Conformes</span><span className="font-bold text-red-600">{riskReport.summary.rgpc_compliance.non_compliant}</span></div>
                    <div className="flex items-center justify-between"><span>Pendentes de Análise</span><span className="font-bold text-gray-600">{riskReport.summary.rgpc_compliance.pending}</span></div>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border bg-card overflow-hidden">
                <div className="p-4 border-b border-border"><h2 className="text-lg font-semibold">Top 10 Entidades com Maior Risco</h2></div>
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead><tr><th>Entidade</th><th>Tipo</th><th>NIF</th><th>Score</th><th>Nível</th><th>Conflitos</th></tr></thead>
                    <tbody>
                      {riskReport.top_risks.map((entity, idx) => (
                        <tr key={`${entity.entity_type}-${entity.entity_id}`} data-testid={`risk-row-${idx}`}>
                          <td className="font-medium">{entity.entity_name}</td>
                          <td>{getTypeLabel(entity.entity_type)}</td>
                          <td className="text-muted-foreground">{entity.tax_id || '—'}</td>
                          <td>
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-2 bg-muted rounded-full overflow-hidden"><div className={`h-full ${entity.risk_score >= 70 ? 'bg-red-500' : entity.risk_score >= 50 ? 'bg-orange-500' : entity.risk_score >= 25 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${entity.risk_score}%` }}></div></div>
                              <span className="text-sm font-medium">{entity.risk_score}</span>
                            </div>
                          </td>
                          <td><span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(entity.risk_level)}`}>{translateRisk(entity.risk_level).toUpperCase()}</span></td>
                          <td className="text-center">{entity.conflicts_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {riskReport.recommendations.length > 0 && (
                <div className="rounded-xl border bg-card p-6">
                  <h2 className="text-lg font-semibold mb-4">Recomendações</h2>
                  <div className="space-y-4">
                    {riskReport.recommendations.map((rec, idx) => (
                      <div key={idx} className={`p-4 rounded-lg border ${rec.priority === 'critical' ? 'border-red-200 bg-red-50 dark:bg-red-900/10' : rec.priority === 'high' ? 'border-orange-200 bg-orange-50 dark:bg-orange-900/10' : 'border-amber-200 bg-amber-50 dark:bg-amber-900/10'}`}>
                        <div className="flex items-start gap-3">
                          <span className={`px-2 py-1 rounded text-xs font-bold ${getRiskColor(rec.priority)}`}>{rec.priority.toUpperCase()}</span>
                          <div><p className="font-medium">{rec.title}</p><p className="text-sm text-muted-foreground mt-1">{rec.description}</p><p className="text-sm mt-2"><strong>Ação:</strong> {rec.action}</p></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (<div className="text-center py-12"><p className="text-muted-foreground">Não foi possível carregar o relatório de risco</p></div>)}
        </div>
      )}

      {activeTab === 'compliance' && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <div className="rounded-xl border bg-card">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h2 className="font-semibold">Relatórios Anteriores</h2>
                <button onClick={generateReport} disabled={generating} className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors flex items-center gap-1 disabled:opacity-50" data-testid="generate-report-btn">
                  {generating ? (<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground"></div>) : (<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>)}
                  Gerar
                </button>
              </div>
              <div className="max-h-[500px] overflow-y-auto">
                {loading ? (<div className="p-8 text-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div></div>) : reports.length === 0 ? (<p className="p-4 text-center text-muted-foreground text-sm">Nenhum relatório gerado</p>) : (
                  <div className="divide-y divide-border">
                    {reports.map((report) => (
                      <button key={report.report_id} onClick={() => viewReport(report.report_id)} className={`w-full p-4 text-left hover:bg-muted/50 transition-colors ${selectedReport?.report_id === report.report_id ? 'bg-muted/50' : ''}`}>
                        <p className="font-medium text-sm">Relatório de Compliance</p>
                        <p className="text-xs text-muted-foreground">{new Date(report.generated_at).toLocaleDateString('pt-PT', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="lg:col-span-2">
            <div className="rounded-xl border bg-card">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h2 className="font-semibold">Pré-visualização do Relatório</h2>
                {selectedReport && (
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">Gerado em {new Date(selectedReport.generated_at).toLocaleString('pt-PT')}</span>
                    <button onClick={() => downloadPdf(selectedReport.report_id)} className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors flex items-center gap-1" data-testid="download-pdf-btn">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                      Download PDF
                    </button>
                  </div>
                )}
              </div>
              <div className="p-6">
                {!selectedReport ? (
                  <div className="text-center py-12">
                    <svg className="w-12 h-12 mx-auto text-muted-foreground mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    <p className="text-muted-foreground">Selecione um relatório ou gere um novo</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {selectedReport.stats && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="p-4 rounded-lg bg-muted/50"><p className="text-2xl font-bold">{selectedReport.stats.total_clients}</p><p className="text-xs text-muted-foreground">Clientes</p></div>
                        <div className="p-4 rounded-lg bg-muted/50"><p className="text-2xl font-bold">{selectedReport.stats.total_suppliers}</p><p className="text-xs text-muted-foreground">Fornecedores</p></div>
                        <div className="p-4 rounded-lg bg-muted/50"><p className="text-2xl font-bold">{selectedReport.stats.total_employees}</p><p className="text-xs text-muted-foreground">Colaboradores</p></div>
                        <div className="p-4 rounded-lg bg-muted/50"><p className="text-2xl font-bold text-red-500">{selectedReport.stats.active_conflicts}</p><p className="text-xs text-muted-foreground">Conflitos Ativos</p></div>
                      </div>
                    )}
                    <div className="prose prose-sm dark:prose-invert max-w-none">
                      <pre className="whitespace-pre-wrap text-sm bg-muted/30 p-4 rounded-lg overflow-auto max-h-[400px]">{selectedReport.content}</pre>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
